import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { UserInfoService } from '@telecom/shared/domain/permission';
import { HeaderServiceProxy } from '@telecom/shared/services/header-service/header-service-proxy';
import { getIsOversea } from '@telecom/shared/utils/environment';
import {
    EurekaAllHistoryService,
    type EurekaAllHistoryService as EurekaAllHistoryServiceType,
} from '@/domains/task/services/eureka-all-history-service';
import {
    OnboardingBrowserRuntime,
    type OnboardingBrowserRuntime as OnboardingBrowserRuntimeType,
} from '../onboarding-browser-runtime';
import type { OnboardingEligibilityService } from '../onboarding-eligibility-service';

const ENTRY_ELIGIBILITY_STORAGE_PREFIX = 'onboarding-eligibility-v2';
const REPORT_ELIGIBILITY_STORAGE_PREFIX = 'onboarding-report-eligibility-v1';
const ELIGIBLE_SNAPSHOT = 'eligible';
const INELIGIBLE_SNAPSHOT = 'ineligible';
const ENTRY_SEEN_SNAPSHOT = 'entry-seen';
const ONBOARDING_START_DELAY_MS = 1000;

/** 分别处理启动页和报告页的账号资格，并复用统一的公共受众判断。 */
@injectable()
export class OnboardingEligibilityServiceImpl implements OnboardingEligibilityService {
    /** 同一账号正在执行的启动页资格判断。 */
    private readonly _pendingEntryEvaluations = new Map<string, Promise<boolean>>();

    /** 同一账号正在执行的报告页资格判断。 */
    private readonly _pendingReportEvaluations = new Map<string, Promise<boolean>>();

    // eslint-disable-next-line max-params
    constructor(
        /** 提供当前账号的个人或企业租户判断。 */
        @inject(UserInfoService)
        private readonly _userInfoService: UserInfoService,
        /** 提供当前账号的同步登录状态和账号 ID。 */
        @inject(HeaderServiceProxy)
        private readonly _headerServiceProxy: HeaderServiceProxy,
        /** 提供当前账号的 Eureka 全部历史任务及总数。 */
        @inject(EurekaAllHistoryService)
        private readonly _eurekaAllHistoryService: EurekaAllHistoryServiceType,
        /** 提供资格快照的安全浏览器存储和异常记录能力。 */
        @inject(OnboardingBrowserRuntime)
        private readonly _runtime: OnboardingBrowserRuntimeType,
    ) {}

    /** 判断启动页资格并按账号固化入口历史快照。 */
    async isEligible(): Promise<boolean> {
        if (!this._isCommonEligible()) return false;

        const storageKey = this._getEntryStorageKey(this._getAccountId());
        const pendingEvaluation = this._pendingEntryEvaluations.get(storageKey);
        if (pendingEvaluation) return pendingEvaluation;

        const evaluation = this._evaluateEntryEligibility(storageKey)
            .then(isEligible => this._waitForStartDelay(isEligible));
        this._pendingEntryEvaluations.set(storageKey, evaluation);
        try {
            return await evaluation;
        }
        finally {
            if (this._pendingEntryEvaluations.get(storageKey) === evaluation) {
                this._pendingEntryEvaluations.delete(storageKey);
            }
        }
    }

    /** 判断报告页资格并按账号固化独立的报告历史快照。 */
    async isReportEligible(): Promise<boolean> {
        if (!this._isCommonEligible()) return false;

        const storageKey = this._getReportStorageKey(this._getAccountId());
        const pendingEvaluation = this._pendingReportEvaluations.get(storageKey);
        if (pendingEvaluation) return pendingEvaluation;

        const evaluation = this._evaluateReportEligibility(storageKey)
            .then(isEligible => this._waitForStartDelay(isEligible));
        this._pendingReportEvaluations.set(storageKey, evaluation);
        try {
            return await evaluation;
        }
        finally {
            if (this._pendingReportEvaluations.get(storageKey) === evaluation) {
                this._pendingReportEvaluations.delete(storageKey);
            }
        }
    }

    /** 将全局入口快照从可展示更新为已展示。 */
    markEntryOnboardingSeen(): void {
        const accountId = this._getAccountId();
        if (!accountId) return;

        const storageKey = this._getEntryStorageKey(accountId);
        try {
            const snapshot = this._runtime.getStorageItem(storageKey);
            if (snapshot !== ELIGIBLE_SNAPSHOT && snapshot !== ENTRY_SEEN_SNAPSHOT) return;
            this._runtime.setStorageItem(storageKey, ENTRY_SEEN_SNAPSHOT);
        }
        catch (error) {
            this._runtime.warn('Failed to mark entry onboarding as seen', error);
        }
    }

    /** 复用登录、账号 ID、海外区域和个人租户四项公共资格判断。 */
    private _isCommonEligible(): boolean {
        const account = this._headerServiceProxy.headerService.account;
        return Boolean(
            account?.isLogin
            && this._getAccountId()
            && getIsOversea()
            && this._userInfoService.isPersonalTenant(),
        );
    }

    /** 读取当前登录账号的稳定 ID。 */
    private _getAccountId(): string {
        return String(this._headerServiceProxy.headerService.account?._info.account_id ?? '');
    }

    /** 生成版本化且按账号隔离的全局入口资格键。 */
    private _getEntryStorageKey(accountId: string): string {
        return `${ENTRY_ELIGIBILITY_STORAGE_PREFIX}:${encodeURIComponent(accountId)}`;
    }

    /** 生成版本化且按账号隔离的全局报告资格键。 */
    private _getReportStorageKey(accountId: string): string {
        return `${REPORT_ELIGIBILITY_STORAGE_PREFIX}:${encodeURIComponent(accountId)}`;
    }

    /** 仅在资格通过时等待统一的 onboarding 启动延时。 */
    private async _waitForStartDelay(isEligible: boolean): Promise<boolean> {
        if (!isEligible) return false;

        try {
            await new Promise<void>((resolve) => {
                this._runtime.setTimeout(resolve, ONBOARDING_START_DELAY_MS);
            });
            return true;
        }
        catch (error) {
            this._runtime.warn('Failed to wait for onboarding start delay', error);
            return false;
        }
    }

    /** 启动页复用已有快照，或查询历史后持久化新的资格分类。 */
    private async _evaluateEntryEligibility(storageKey: string): Promise<boolean> {
        try {
            const snapshot = this._runtime.getStorageItem(storageKey);
            if (snapshot === ELIGIBLE_SNAPSHOT) return true;
            if (snapshot === INELIGIBLE_SNAPSHOT || snapshot === ENTRY_SEEN_SNAPSHOT) return false;

            const hasHistory = await this._eurekaAllHistoryService.hasHistory();
            this._runtime.setStorageItem(
                storageKey,
                hasHistory ? INELIGIBLE_SNAPSHOT : ELIGIBLE_SNAPSHOT,
            );
            return !hasHistory;
        }
        catch (error) {
            this._runtime.warn('Failed to resolve onboarding eligibility', error);
            return false;
        }
    }

    /** 报告页复用独立快照，或排除当前任务后固化新的资格分类。 */
    private async _evaluateReportEligibility(storageKey: string): Promise<boolean> {
        try {
            const snapshot = this._runtime.getStorageItem(storageKey);
            if (snapshot === ELIGIBLE_SNAPSHOT) return true;
            if (snapshot === INELIGIBLE_SNAPSHOT) return false;

            const historyTotal = await this._eurekaAllHistoryService.getHistoryTotal();
            const isEligible = historyTotal <= 1;
            this._runtime.setStorageItem(
                storageKey,
                isEligible ? ELIGIBLE_SNAPSHOT : INELIGIBLE_SNAPSHOT,
            );
            return isEligible;
        }
        catch (error) {
            this._runtime.warn('Failed to resolve report onboarding eligibility', error);
            return false;
        }
    }
}
